// ScalaImport renders the Scala/Ableton import-export workspace.
// It exposes raw text editing, validation warnings, and file export/import
// actions without owning the canonical parsed scale representation itself.

import { createRef } from "preact";
import { useEffect, useState } from "preact/hooks";
import PropTypes from "prop-types";
import {
  settingsToPlainScala,
  settingsToAbletonScala,
  settingsToKbm,
  settingsToPresetJson,
  hasNegativeScalaExportValues,
} from "./parse-scale";

// Trigger a file download in the browser
const downloadFile = (content, filename, mimeType = "text/plain") => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const safeName = (settings) => (settings.name || "custom").replace(/[^a-zA-Z0-9_\-]/g, "_");

const ScalaImport = (props) => {
  const fileInputRef = createRef();
  const hasNegativeExportValues = hasNegativeScalaExportValues(props.settings);
  const [scaleImportDraft, setScaleImportDraft] = useState(props.settings.scale_import || "");

  useEffect(() => {
    setScaleImportDraft(props.settings.scale_import || "");
  }, [props.settings.scale_import]);

  const commitScaleImportDraft = (value) => {
    if (value === (props.settings.scale_import || "")) return;
    props.onChange("scale_import", value);
  };

  const handleFileOpen = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const nextValue = String(ev.target.result ?? "");
      setScaleImportDraft(nextValue);
      props.onChange("scale_import", nextValue);
    };
    reader.readAsText(file);
    // Reset so the same file can be re-opened if needed
    e.target.value = "";
  };

  const name = safeName(props.settings);

  return (
    <div class="settings-form__stack settings-form__stack--import">
      {/* ── Import section ─────────────────────────────────────────────── */}
      <fieldset class="settings-panel">
        <legend>
          <b>Scala File</b>
        </legend>
        <button type="button" class="settings-panel__close" onClick={props.onCancel} title="Close">
          ✕
        </button>
        <p>
          copy/paste or type below using the Scala file format:&nbsp;
          <a href="http://www.huygens-fokker.org/scala/scl_format.html">[Scala format]</a>&nbsp;
          <a href="https://scaleworkshop.plainsound.org">[Scale Workshop]</a>
        </p>
        <p>
          <b>Name</b> <em>(optional):</em> "!" followed by scala file name, e.g. "! myScale.scl"
          <br />
          <b>!</b> precedes a comment or empty line
          <br />
          <b>Description</b>: some text about the scale
          <br />
          <b>Size</b>: the number of scale degrees
          <br />
          <b>Scale</b>: a list of ratios (b/a) or cents (numbers with a decimal point)
        </p>
        <label>
          <textarea
            name="scale_import"
            onInput={(e) => setScaleImportDraft(e.currentTarget.value)}
            onBlur={(e) => commitScaleImportDraft(e.currentTarget.value)}
            value={scaleImportDraft}
          />
        </label>
        <br />
        {/* Hidden file input — triggered by the Open button */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".scl,.ascl"
          class="settings-form__hidden-file-input"
          onChange={handleFileOpen}
        />
        <button type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
          Open .scl / .ascl file
        </button>
        &nbsp;&nbsp;
        <button
          type="button"
          onClick={() => {
            commitScaleImportDraft(scaleImportDraft);
            props.onImport(scaleImportDraft);
          }}
        >
          Build Layout
        </button>
      </fieldset>

      {/* ── Export section ─────────────────────────────────────────────── */}
      <fieldset class="settings-panel">
        <legend>
          <b>Export</b>
        </legend>
        {hasNegativeExportValues ? (
          <p class="settings-form__warning-copy">
            Negative scale values are not supported in Scala export. Plain Scala and Ableton Scala
            export are disabled until those values are removed.
          </p>
        ) : null}
        <p>
          <b>Plain Scala</b> — standard .scl format, compatible with all Scala-aware software
        </p>
        <button
          type="button"
          disabled={hasNegativeExportValues}
          onClick={() => downloadFile(settingsToPlainScala(props.settings), `${name}.scl`)}
        >
          Save .scl
        </button>
        &nbsp;&nbsp;
        <button
          type="button"
          onClick={() => downloadFile(settingsToKbm(props.settings), `${name}.kbm`)}
        >
          Save .kbm
        </button>
        <p>
          <b>Ableton Scala</b> — .ascl format, embeds the .kbm data in comments
        </p>
        <button
          type="button"
          disabled={hasNegativeExportValues}
          onClick={() => downloadFile(settingsToAbletonScala(props.settings), `${name}.ascl`)}
        >
          Save .ascl
        </button>
        <p>
          <b>User Preset JSON</b> — export current Tuning as a JSON file
        </p>
        <button
          type="button"
          onClick={() =>
            downloadFile(
              settingsToPresetJson(
                props.settings,
                props.settings.key_labels === "heji" && props.heji_names?.length
                  ? { note_names: props.heji_names }
                  : {},
              ),
              `${name}.json`,
              "application/json",
            )
          }
        >
          Save .json
        </button>
      </fieldset>
    </div>
  );
};

ScalaImport.propTypes = {
  onChange: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  heji_names: PropTypes.arrayOf(PropTypes.string),
};

export default ScalaImport;
